import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { DocsPageShell, DOC_NAV, type DocBlock } from "@/components/marketing/DocsLayout";

const blocks: DocBlock[] = [
  { type: "h2", text: "What Reality Twin is" },
  {
    type: "p",
    text: "Reality Twin turns your website into an AI Twin: a conversational, always-on digital version of your company, grounded in your own knowledge. It answers visitors, qualifies and captures leads, and publishes a public profile so other companies' twins can discover you.",
  },
  {
    type: "p",
    text: (
      <>
        These docs cover the real, shipped features — how to build a twin, teach it, deploy it, and
        connect it to the tools you already use. New to the product? Start with the{" "}
        <Link to="/docs/quickstart" className="underline hover:text-foreground">
          Quickstart
        </Link>
        .
      </>
    ),
  },
  { type: "h2", text: "How the pieces fit" },
  {
    type: "ul",
    items: [
      <>
        <strong className="text-foreground">Knowledge base</strong> — your website (crawled
        automatically) plus uploaded documents become a citable knowledge graph the twin reasons
        against.
      </>,
      <>
        <strong className="text-foreground">The twin</strong> — a reasoning agent with a personality
        you tune; every answer is grounded and cited, and it qualifies leads as it talks.
      </>,
      <>
        <strong className="text-foreground">Deploy surfaces</strong> — an embeddable chat widget, a
        public marketplace profile, a REST/chat endpoint, and an MCP server for AI assistants.
      </>,
      <>
        <strong className="text-foreground">Leads & CRM</strong> — captured leads stream to your CRM
        via an HMAC-signed webhook.
      </>,
      <>
        <strong className="text-foreground">The network</strong> — the Business Exchange matches
        listings between companies, and twins can meet other twins.
      </>,
    ],
  },
  { type: "h2", text: "Where to go next" },
  {
    type: "table",
    head: ["If you want to…", "Read"],
    rows: [
      [
        "Launch your first twin",
        <Link to="/docs/quickstart" className="underline">
          Quickstart
        </Link>,
      ],
      [
        "Teach the twin",
        <Link to="/docs/knowledge-base" className="underline">
          Knowledge base
        </Link>,
      ],
      [
        "Put it on your site",
        <Link to="/docs/embed-widget" className="underline">
          Embed widget
        </Link>,
      ],
      [
        "Stream leads to your CRM",
        <Link to="/docs/leads-and-crm" className="underline">
          Leads & CRM
        </Link>,
      ],
      [
        "Connect ChatGPT / Claude",
        <Link to="/docs/mcp-server" className="underline">
          MCP server
        </Link>,
      ],
      [
        "Understand pricing & limits",
        <Link to="/docs/plans-and-usage" className="underline">
          Plans & usage
        </Link>,
      ],
    ],
  },
  {
    type: "callout",
    title: "Something missing?",
    text: (
      <>
        These docs track shipped features and grow every week. If you can't find something, open a
        ticket from your dashboard's Help page or write to hello@realitytwin.io.
      </>
    ),
  },
];

const meta = DOC_NAV[0].items[0];

export const Route = createFileRoute("/docs")({
  head: () =>
    buildSeo({
      path: "/docs",
      title: "Documentation — Reality Twin",
      description:
        "Reality Twin developer & product documentation: quickstart, knowledge base, embed widget, lead webhooks, MCP server, the Exchange, plans and security.",
    }),
  component: () => (
    <DocsPageShell slug="" title="Documentation" description={meta.description} blocks={blocks} />
  ),
});
